/* eslint-disable @typescript-eslint/ban-types */
import { useEffect, useMemo } from 'react';
import { Pagination } from '@grupoboticario/flora-react';
import {
  useTable,
  usePagination,
  useSortBy,
  Column,
  useFlexLayout,
  useFilters,
  Filters,
  SortingRule,
} from 'react-table';
import { ReactSelectFlora } from 'components/ReactSelectFlora';
import Skeleton from 'react-loading-skeleton';
import { SortIcons } from './SortIcons';
import { TableStyled, Container, PageOptions } from './styles';

interface TableProps<T extends object> {
  columns: ReadonlyArray<Column<T>>;
  data: T[];
  columnFilters: Filters<T>;
  hiddenColumns?: string[];
  defaultSortBy?: SortingRule<T>[];
  sorted?: boolean;
}

export const Table = <T extends object>({
  columns,
  data,
  columnFilters,
  defaultSortBy,
  hiddenColumns = [],
  sorted = false,
}: TableProps<T>) => {
  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headerGroups,
    page,
    setAllFilters,
    filteredRows,
    setPageSize,
    gotoPage,
    state: { pageIndex, pageSize },
  } = useTable<T>(
    {
      columns,
      data,
      initialState: {
        pageSize: 5,
        filters: columnFilters,
        hiddenColumns,
        sortBy: defaultSortBy,
      },
      disableSortBy: !sorted,
      disableSortRemove: true,
    },
    useFilters,
    useSortBy,
    usePagination,
    useFlexLayout,
  );
  const pageSizeOptions = useMemo(
    () => [
      { value: 5, label: '5' },
      { value: 10, label: '10' },
      { value: 15, label: '15' },
    ],
    [],
  );

  useEffect(() => {
    setAllFilters(columnFilters);
  }, [setAllFilters, columnFilters]);

  useEffect(() => {
    gotoPage(0);
  }, [filteredRows]);

  return (
    <Container>
      <TableStyled {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps(
                    column.getSortByToggleProps({ title: undefined }),
                  )}
                >
                  <div>
                    {column.render('Header') as React.ReactNode}
                    {sorted && !column.disableSortBy && (
                      <SortIcons
                        isSorted={column.isSorted}
                        isSortedDesc={column.isSortedDesc}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.length > 0 ? (
            page.map(row => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map(cell => {
                    return (
                      <td {...cell.getCellProps()}>
                        {cell.render('Cell') as React.ReactNode}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          ) : (
            <tr>
              <td aria-label="loading...">
                <Skeleton
                  count={5}
                  style={{ height: '56px', marginBottom: '0.5rem' }}
                />
              </td>
            </tr>
          )}
        </tbody>
      </TableStyled>
      <footer>
        <PageOptions>
          Mostrando
          <ReactSelectFlora
            label=""
            value={pageSizeOptions.filter(option => option.value === pageSize)}
            options={pageSizeOptions}
            onChangeSelect={
              (option: { value: number }) => setPageSize(option.value)
              // eslint-disable-next-line react/jsx-curly-newline
            }
          />
          de <span className="page-option"> {filteredRows.length} </span>{' '}
          registros
        </PageOptions>
        <Pagination
          page={pageIndex}
          itemsPerPage={pageSize}
          totalRecords={filteredRows.length}
          onChange={(pageNumber: any) => gotoPage(pageNumber)}
        />
      </footer>
    </Container>
  );
};
